import { PageHeaderFilterBarSlotProvider } from "@repo/ui/Navigation/pageHeaderFilterBarSlot";
import { PageWidth, pageWidthClassNames } from "@repo/ui/Navigation/pageWidth";
import {
  createContext,
  useCallback,
  useContext,
  useLayoutEffect,
  useRef,
  useState,
  type FC,
  type PropsWithChildren,
  type ReactNode,
} from "react";
import { twMerge } from "tailwind-merge";
import { tv } from "tailwind-variants";

/**
 * How a page's shell relates to the app's main scroll container. Leave unset
 * for the common case: the page flows and the main container scrolls.
 */
export const PageLayoutHeight = {
  /**
   * At least the container's height, so a `PageBody fills` child can grow and
   * anchor inner sticky elements (e.g. a sticky sidebar). Still flows.
   */
  Fills: "fills",
  /**
   * Exactly the container's height, so a `PageBody fills` child can hold the
   * one element that owns vertical scrolling: a `TableCard` with sticky
   * headers and pinned columns, or an editor's panes.
   */
  Bounded: "bounded",
} as const;

export type PageLayoutHeight =
  (typeof PageLayoutHeight)[keyof typeof PageLayoutHeight];

export interface PageLayoutProps extends PropsWithChildren {
  height?: PageLayoutHeight;
  className?: string;
}

/**
 * The outer shell of a standard page beneath the sticky `PageHeader`. A page
 * either flows in the main scroll container or is bounded to it with exactly
 * one inner scroller; never add a second vertical scroller below the shell,
 * which breaks sticky headers and scroll restoration (`useNavigationScroll`
 * tracks the main container only).
 */
export const PageLayout: FC<PageLayoutProps> = ({
  height,
  className,
  children,
}) => (
  <div
    className={twMerge(
      "flex flex-col",
      height === PageLayoutHeight.Bounded && "h-full",
      height === PageLayoutHeight.Fills && "min-h-full",
      className,
    )}
  >
    {children}
  </div>
);

const pageBodyStyle = tv({
  base: "flex flex-col gap-4 px-4 py-4 sm:gap-6 sm:px-6 sm:py-6",
  variants: {
    fills: {
      true: "min-h-0 flex-1",
    },
  },
});

export interface PageBodyProps extends PropsWithChildren {
  /** Grow to fill a `PageLayout height` shell. */
  fills?: boolean;
  /**
   * The content column the body is centered in. Pass the same value to the
   * page's `PageHeader` so the two stay aligned.
   * @default PageWidth.Full
   */
  width?: PageWidth;
  className?: string;
}

/**
 * The standard content wrapper directly below the page header area: the page
 * gutter padding and vertical rhythm every page shares.
 */
export const PageBody: FC<PageBodyProps> = ({
  fills = false,
  width = PageWidth.Full,
  className,
  children,
}) => (
  <div
    className={pageBodyStyle({
      fills,
      className: twMerge(
        pageWidthClassNames[width],
        // Keeps settings forms from collapsing below a usable width. The
        // header shares the column but not the floor, so its title row can
        // still shrink.
        width === PageWidth.Form && "min-w-[450px]",
        className,
      ),
    })}
  >
    {children}
  </div>
);

const FullHeightPageContentContext = createContext<(() => () => void) | null>(
  null,
);

export interface TabbedPageLayoutProps extends PropsWithChildren {
  /** The page's `PageHeader` (typically with `tabs` and a `PageHeaderFilterBarSlot`). */
  header: ReactNode;
}

/**
 * Shell for a tabbed page whose child routes are mostly flowing pages but may
 * include bounded full-height experiences. Child routes declare themselves
 * via `FullHeightPageContent`; the shell then switches from a flow column to
 * a full-height grid — same tree, only the classes change, so the header and
 * any providers keep their state. The full-height region scrolls horizontally
 * for wide editors but never vertically: the child fills it (`h-full`) and
 * owns vertical scrolling. Includes the `PageHeaderFilterBarSlotProvider` so
 * tabs can render their filter bars into the header's slot.
 */
export const TabbedPageLayout: FC<TabbedPageLayoutProps> = ({
  header,
  children,
}) => {
  const [fullHeightCount, setFullHeightCount] = useState(0);
  const register = useCallback(() => {
    setFullHeightCount((count) => count + 1);
    return () => setFullHeightCount((count) => count - 1);
  }, []);

  const isFullHeight = fullHeightCount > 0;

  return (
    <PageHeaderFilterBarSlotProvider>
      <FullHeightPageContentContext.Provider value={register}>
        <div
          className={
            isFullHeight
              ? "grid h-full grid-rows-[auto_minmax(0,1fr)] overflow-clip"
              : "flex flex-col"
          }
        >
          {header}
          <div
            className={
              isFullHeight
                ? "relative overflow-x-auto overflow-y-hidden"
                : "contents"
            }
          >
            {children}
          </div>
        </div>
      </FullHeightPageContentContext.Provider>
    </PageHeaderFilterBarSlotProvider>
  );
};

/**
 * Declares the enclosing child route as a bounded full-height experience: the
 * ancestor `TabbedPageLayout` gives it a definite-height region to fill
 * (`h-full`) instead of the flowing page column; the child owns vertical
 * scrolling. Registration happens in a layout effect, so the shell switches
 * before paint.
 */
export const FullHeightPageContent: FC<PropsWithChildren> = ({ children }) => {
  const register = useContext(FullHeightPageContentContext);
  useLayoutEffect(() => {
    if (!register) {
      throw new Error(
        "FullHeightPageContent requires a TabbedPageLayout ancestor.",
      );
    }
    return register();
  }, [register]);
  return <>{children}</>;
};

export interface TableCardProps extends PropsWithChildren {
  className?: string;
}

const HORIZONTAL_SCROLL_EDGE_THRESHOLD_PX = 1;

/**
 * Bordered scroll container that frames a full-bleed table inside a `PageBody`.
 * Under `PageLayoutHeight.Bounded` it owns both scroll axes, so `EntityTable`'s
 * sticky header and pinned columns work; in a flowing layout it grows with the
 * table, scrolls horizontally only, and the sticky header is inert. `relative`
 * keeps react-aria's hidden inputs and `sr-only` text within the card's
 * overflow rather than an ancestor's.
 *
 * While the table overflows horizontally, each edge fades out wherever more
 * content can be scrolled into view. The fades are sticky inside the scroll
 * container, so the browser paints its scrollbars over them. A table with
 * pinned columns gets no left fade; its last pinned column draws its own.
 */
export const TableCard: FC<TableCardProps> = (props) => {
  const { className, children } = props;
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const [hasHorizontalOverflow, setHasHorizontalOverflow] = useState(false);

  useLayoutEffect(() => {
    const scrollContainer = scrollContainerRef.current;
    const content = contentRef.current;
    if (!scrollContainer || !content) {
      return;
    }

    const updateOverflow = () => {
      setHasHorizontalOverflow(
        scrollContainer.scrollWidth - scrollContainer.clientWidth >
          HORIZONTAL_SCROLL_EDGE_THRESHOLD_PX,
      );
    };

    updateOverflow();
    // The content shares a grid cell with the fades, so it is as wide as the
    // table and resizes whenever the table does.
    const resizeObserver = new ResizeObserver(updateOverflow);
    resizeObserver.observe(scrollContainer);
    resizeObserver.observe(content);

    return () => resizeObserver.disconnect();
  }, []);

  // Each fade's opacity follows the card's horizontal scroll position. The
  // sticky gray strip matches `EntityTable`'s 40px header, following it when
  // the header sticks.
  const fadeClassName =
    "pointer-events-none sticky z-10 col-start-1 row-start-1 w-6 bg-white opacity-0 [animation-fill-mode:both] [animation-timeline:scroll(x)] [animation-timing-function:linear]";
  const fadeHeader = <div className="sticky top-0 h-10 bg-gray-50" />;

  return (
    <div
      ref={scrollContainerRef}
      className={twMerge(
        "group/table-card relative isolate grid min-h-0 max-w-full overflow-auto overscroll-x-none rounded-lg border border-border-light bg-white",
        className,
      )}
    >
      <div ref={contentRef} className="col-start-1 row-start-1">
        {children}
      </div>
      <div
        aria-hidden
        className={twMerge(
          fadeClassName,
          !hasHorizontalOverflow && "hidden",
          "left-0 justify-self-start [mask-image:linear-gradient(to_right,black,transparent)] [animation-name:fade-in] [animation-range:0px_24px] group-has-[[data-pinned-columns]]/table-card:hidden",
        )}
      >
        {fadeHeader}
      </div>
      <div
        aria-hidden
        className={twMerge(
          fadeClassName,
          !hasHorizontalOverflow && "hidden",
          "right-0 justify-self-end [mask-image:linear-gradient(to_left,black,transparent)] [animation-name:fade-out] [animation-range:calc(100%-24px)_100%]",
        )}
      >
        {fadeHeader}
      </div>
    </div>
  );
};
